import { useApiMutation } from '@/hooks/useApi';
import { useProfile } from '@/hooks/useProfile';
import {
  createMessageFeedbackPayload,
  deleteMessageFeedbackPayload,
} from '@/interface/chat';
import {
  StopCircleOutlined,
  ThumbDown,
  ThumbDownOutlined,
  ThumbUp,
  ThumbUpOutlined,
  VolumeUpOutlined,
  ContentCopyOutlined,
  CheckCircleOutline,
} from '@mui/icons-material';
import {
  Box,
  IconButton,
  Snackbar,
  styled,
  Tooltip,
  tooltipClasses,
  useTheme,
} from '@mui/material';
import type { TooltipProps } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import i18next from 'i18next';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { colors } from '../../theme/colors';
import FeedbackFormContainer from './FeedbackFormContainer';

export const ThemedTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.tooltips.main,
    color: theme.palette.mode === 'dark' ? colors.primary : colors.white,
    boxShadow: theme.shadows[1],
    fontSize: 11,
    textTransform: 'capitalize',
  },
}));

const CustomSnackbar = styled(Snackbar)(({ theme }) => ({
  '& .MuiSnackbarContent-root': {
    backgroundColor: theme.palette.tooltips.main,
    color: theme.palette.mode === 'dark' ? colors.primary : colors.white,
  },
}));

const IconButtonWithTooltip = ({
  title,
  onClick,
  icon,
  isDisabled = false,
}: {
  title: string;
  onClick: () => void;
  icon: React.ReactNode;
  isDisabled?: boolean;
}) => {
  const [open, setOpen] = useState(false);
  return (
    <ThemedTooltip
      key={title}
      title={title}
      placement="top-start"
      enterDelay={300}
      leaveDelay={200}
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      disableInteractive
    >
      <IconButton
        onClick={() => {
          setOpen(false);
          onClick();
        }}
        onMouseLeave={() => setOpen(false)}
        size="small"
        disabled={isDisabled}
      >
        {icon}
      </IconButton>
    </ThemedTooltip>
  );
};

type Props = {
  conversationId: string;
  chatId: string;
  responseText: string;
  feedback: '👍🏻' | '👎🏻' | undefined;
  isStaffMode?: boolean;
};

const FeedbackContainer: React.FC<Props> = ({
  conversationId,
  chatId,
  responseText = '',
  feedback,
  isStaffMode = false,
}) => {
  const queryClient = useQueryClient();
  const { profile } = useProfile();
  const { t } = useTranslation(['chat']);
  const theme = useTheme();
  const [showDislikeModal, setShowDislikeModal] = useState(false);
  const [feedbackState, setFeedBackState] = useState<'👍🏻' | '👎🏻' | undefined>(
    feedback,
  );
  const [isApiCallDone, setApiCallDone] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '' });
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [copied, setCopied] = useState(false);
  const userId = profile?.value[0].eXpMember.Uuid as string;
  const color =
    theme.palette.mode === 'dark'
      ? colors.feedBackIconsButton.dark.background
      : colors.feedBackIconsButton.light.background;

  const { mutateAsync: addFeedback } = useApiMutation<
    void,
    createMessageFeedbackPayload
  >({
    url: '/user/addFeedback',
    method: 'post',
    options: { retry: 2 },
  });
  const { mutateAsync: updateFeedback } = useApiMutation<
    void,
    createMessageFeedbackPayload
  >({
    url: '/user/updateFeedback',
    method: 'put',
    options: { retry: 2 },
  });
  const { mutateAsync: deleteFeedback } = useApiMutation<
    void,
    deleteMessageFeedbackPayload
  >({
    url: '/user/deleteFeedback',
    method: 'delete',
    options: { retry: 2 },
    useParams: true,
  });

  const addFeedbackFun = async (currFeedback: '👍🏻' | '👎🏻') => {
    try {
      await addFeedback({
        userId: userId,
        messageId: chatId,
        conversationId: conversationId,
        type: currFeedback,
        deviceType: 'Desktop',
        userName:
          profile?.value[0].MemberFirstName +
          ' ' +
          profile?.value[0].MemberLastName,
        userEmail: profile?.value[0].MemberEmail + '',
      });
      setApiCallDone(true);
      if (currFeedback === '👍🏻')
        setSnackbar({ open: true, message: t('feedbackResponse') });
    } catch (error) {
      setFeedBackState(feedback);
      console.error('Error in addFeedbackFun', error);
      if (currFeedback === '👍🏻')
        setSnackbar({ open: true, message: t('feedbackError') });
    }
  };

  const updateFeedbackFun = async (currFeedback: '👍🏻' | '👎🏻') => {
    try {
      await updateFeedback({
        userId: userId,
        messageId: chatId,
        type: currFeedback,
        conversationId: conversationId,
        deviceType: 'Desktop',
        userName:
          profile?.value[0].MemberFirstName +
          ' ' +
          profile?.value[0].MemberLastName,
        userEmail: profile?.value[0].MemberEmail + '',
      });
      setApiCallDone(true);
      if (currFeedback === '👍🏻')
        setSnackbar({ open: true, message: t('feedbackResponse') });
    } catch (error) {
      setFeedBackState(feedback);
      console.error('Error in updatefeedbackFun', error);
      if (currFeedback === '👍🏻')
        setSnackbar({ open: true, message: t('feedbackError') });
    }
  };

  const deleteFeedbackFun = async () => {
    try {
      await deleteFeedback({
        userId: userId,
        messageId: chatId,
        conversationId: conversationId,
        deviceType: 'Desktop',
      });
      setSnackbar({ open: true, message: t('feedbackResponse') });
      setFeedBackState(undefined);
    } catch (error) {
      setFeedBackState(feedback);
      console.error('Error in deleteFeedbackFun', error);
      setSnackbar({ open: true, message: t('feedbackError') });
    }
  };

  const handleFeedbackHandler = async (newFeedback: '👍🏻' | '👎🏻') => {
    try {
      if (feedbackState === newFeedback) {
        setFeedBackState(undefined);
        await deleteFeedbackFun();
      } else if (feedbackState !== undefined) {
        setFeedBackState(newFeedback);
        await updateFeedbackFun(newFeedback);
      } else {
        setFeedBackState(newFeedback);
        await addFeedbackFun(newFeedback);
      }
    } catch (error) {
      console.error('Error in handleFeedbackHandler', error);
    } finally {
      await queryClient.invalidateQueries({
        queryKey: ['/user/fetchAllMessage'],
      });
    }
  };

  const speakText = () => {
    if (!window.speechSynthesis) {
      setSnackbar({
        open: true,
        message: t('speechNotSupported'),
      });
      return;
    }

    if (!responseText) {
      setSnackbar({
        open: true,
        message: t('noTextToSpeak'),
      });
      return;
    }

    if (window.speechSynthesis.speaking || window.speechSynthesis.paused) {
      window.speechSynthesis.cancel();
      setIsSpeaking(false);
      return;
    }

    const utterance = new SpeechSynthesisUtterance(responseText);
    utterance.lang = i18next.language || 'en-US';

    utterance.onstart = () => setIsSpeaking(true);
    utterance.onend = () => setIsSpeaking(false);

    utterance.onerror = (event) => {
      console.error('Speech synthesis error:', event);

      // Only show Snackbar if it's an actual error, not manual stop
      if (event.error !== 'interrupted') {
        setSnackbar({
          open: true,
          message: t('speechError'),
        });
      }

      setIsSpeaking(false);
    };

    window.speechSynthesis.speak(utterance);
  };

  const copyToClipboard = async () => {
    if (!responseText) return;

    try {
      await navigator.clipboard.writeText(responseText);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Copy failed', err);
    }
  };

  return (
    <>
      <Box
        style={{ display: 'flex', justifyContent: 'center', gap: 12 }}
        data-testid="feedback-container"
      >
        <IconButtonWithTooltip
          title={
            isStaffMode
              ? t('staffFeedbackDisabled', 'Feedback disabled in staff mode')
              : t('thumbsUp')
          }
          onClick={() => {
            if (isStaffMode) return;
            if (showDislikeModal) setShowDislikeModal(false);
            handleFeedbackHandler('👍🏻');
          }}
          isDisabled={isStaffMode}
          icon={
            feedbackState === '👍🏻' ? (
              <ThumbUp
                sx={{ color: isStaffMode ? 'grey' : color, fontSize: 16 }}
                data-testid="thumbs-up-filled"
              />
            ) : (
              <ThumbUpOutlined
                sx={{ color: isStaffMode ? 'grey' : color, fontSize: 16 }}
                data-testid="thumbs-up-outline"
              />
            )
          }
        />

        <IconButtonWithTooltip
          title={
            isStaffMode
              ? t('staffFeedbackDisabled', 'Feedback disabled in staff mode')
              : t('thumbsDown')
          }
          onClick={() => {
            if (isStaffMode) return;
            if (feedbackState === '👎🏻') setShowDislikeModal(false);
            if (feedbackState !== '👎🏻') {
              setShowDislikeModal(true);
            }
            handleFeedbackHandler('👎🏻');
          }}
          isDisabled={isStaffMode}
          icon={
            feedbackState === '👎🏻' ? (
              <ThumbDown
                sx={{ color: isStaffMode ? 'grey' : color, fontSize: 16 }}
                data-testid="thumbs-down-filled"
              />
            ) : (
              <ThumbDownOutlined
                sx={{ color: isStaffMode ? 'grey' : color, fontSize: 16 }}
                data-testid="thumbs-down-outline"
              />
            )
          }
        />

        <IconButtonWithTooltip
          title={
            isSpeaking
              ? t('stopSpeaking', 'Stop speaking')
              : t('speakResponse', 'Speak response')
          }
          onClick={speakText}
          icon={
            isSpeaking ? (
              <StopCircleOutlined
                sx={{
                  color,
                  fontSize: 16,
                }}
                data-testid="stop-speaking-icon"
              />
            ) : (
              <VolumeUpOutlined
                sx={{
                  color,
                  fontSize: 16,
                }}
                data-testid="speak-response-icon"
              />
            )
          }
        />

        <ThemedTooltip
          title={
            copied ? t('copied', 'Copied!') : t('copyResponse', 'Copy response')
          }
          placement="top-start"
          open={copied || undefined}
          onOpen={() => {}}
          onClose={() => {}}
          disableInteractive
        >
          <IconButton
            onClick={copyToClipboard}
            size="small"
            onMouseLeave={() => copied && setCopied(false)}
            data-testid="copy-button"
          >
            {copied ? (
              <CheckCircleOutline
                sx={{ color, fontSize: 16 }}
                data-testid="copy-success-icon"
              />
            ) : (
              <ContentCopyOutlined
                sx={{ color, fontSize: 16 }}
                data-testid="copy-icon"
              />
            )}
          </IconButton>
        </ThemedTooltip>

        <CustomSnackbar
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          open={snackbar.open}
          autoHideDuration={3000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          message={snackbar.message}
          key="snackbar"
          data-testid="feedback-snackbar"
        />
      </Box>
      {showDislikeModal && (
        <FeedbackFormContainer
          setSnackbar={setSnackbar}
          onClose={() => setShowDislikeModal(false)}
          userId={userId}
          messageId={chatId}
          disabledSubmitButton={isApiCallDone}
          data-testid="feedback-form-modal"
        />
      )}
    </>
  );
};

export default FeedbackContainer;
